import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { legal } from "../../lib/api";
import { COUNTRIES } from "../../lib/countries";
import { LegalFrameworkCard } from "../../lib/LegalFrameworkCard";
import { useSeo } from "../../lib/useSeo";

interface LegalPageProps {
  title: string;
  effective?: string;
  sections: { heading: string; body: string }[];
  path?: string;
  children?: React.ReactNode;
}

export function LegalPage({ title, effective = "May 18, 2026", sections, path, children }: LegalPageProps) {
  useSeo({
    title: `${title} — NestPlot`,
    description: `${title} for NestPlot property operations software. Effective ${effective}.`,
    path: path ?? (title.toLowerCase().includes("terms") ? "/terms" : title.toLowerCase().includes("privacy") ? "/privacy" : "/legal"),
  });
  return (
    <div className="mx-auto max-w-[760px] px-5 lg:px-8 py-12 lg:py-16">
      <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
        Effective {effective}
      </div>
      <h1 className="t-display-l mt-2">{title}</h1>
      <div className="prose mt-8 space-y-6">
        {sections.map((s) => (
          <section key={s.heading}>
            <h2 className="font-display font-extrabold text-[18px] uppercase tracking-[-0.005em] mb-2">
              {s.heading}
            </h2>
            <p className="text-sub leading-relaxed text-[15px]">{s.body}</p>
          </section>
        ))}
      </div>
      {children}
    </div>
  );
}

/** The country-aware legal-framework block on the public Terms/Privacy pages.
 *
 *  The base agreement above is one document; the privacy law, regulator and
 *  tenancy/storage regime that actually apply depend on where an operator is.
 *  Rather than publish fabricated foreign legal documents, we surface the same
 *  vetted framework the in-app card uses (served by GET /public/legal from
 *  data/countries.py) — the named law + regulator, with no claim that the
 *  operator is compliant, and the explicit US-only statutory-engine line.
 */
function LegalFrameworkSection() {
  // Undefined lets the server resolve the visitor's country from the Cloudflare
  // edge (falls back to US); picking from the select pins it.
  const [country, setCountry] = useState<string | undefined>(undefined);
  const q = useQuery({
    queryKey: ["public-legal", country ?? "auto"],
    queryFn: () => legal.framework(country),
  });
  const selected = country ?? q.data?.country ?? "US";

  return (
    <section className="mt-12 pt-10 border-t border-line">
      <h2 className="font-display font-extrabold text-[18px] uppercase tracking-[-0.005em] mb-2">
        Your legal framework
      </h2>
      <p className="text-sub leading-relaxed text-[15px] mb-5">
        NestPlot serves operators in several countries, and the privacy law,
        regulator, and tenancy or storage regime that apply depend on where you
        operate. Choose your country to see the framework we work under — and,
        honestly, what we do and don't automate under it.
      </p>
      <div className="mb-5 max-w-[320px]">
        <label className="font-mono text-[10px] font-bold tracking-[0.16em] uppercase text-muted mb-1 block">
          Country
        </label>
        <select
          className="w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink"
          value={selected}
          onChange={(e) => setCountry(e.target.value)}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      {q.data && !q.data.is_sellable && (
        <p className="text-sub text-sm mb-5">
          NestPlot isn't available to purchase in {q.data.country_name} yet —
          this framework is shown for operators there who ask about it.
        </p>
      )}
      <LegalFrameworkCard framework={q.data} />
    </section>
  );
}

export function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy"
      sections={[
        {
          heading: "Who we are",
          body: "NestPlot (“we”, “our”, “us”) is a DBA of Digideal LLC, a Delaware-formed limited liability company with a registered office at 16192 Coastal Highway, Lewes, DE 19958-9776. This policy explains what data we collect, why we collect it, who we share it with, and the rights you have over your data when you use nestplot.com or any NestPlot product. NestPlot is a property operations platform used by operators across multifamily, self-storage, parking, and RV/boat properties.",
        },
        {
          heading: "What we collect",
          body: "We collect operator account information (name, email, phone, billing address); property details (units, addresses, access-vendor credentials, dock or lot plans); and customer data you choose to upload or that customers enter on your public rental, application, or permit page. “Customer” here means whoever rents from you — a tenant, a resident, a permit-holder, a boater, or an RV operator. Your customer data is yours — operator-owned rosters can be exported as CSV at any time.",
        },
        {
          heading: "What we don't store",
          body: "We never store raw payment card numbers. All payments are processed through a PCI-compliant payment provider; card data is tokenized in the customer's browser before it reaches our systems. We also do not retain Social Security numbers or full driver's-license images; if a state requires ID verification, the document is forwarded to your identity-verification provider and not stored on our servers. The same applies to vessel documentation, license-plate images, and any other identity artifact passed through to a verification or recovery vendor.",
        },
        {
          heading: "Where it lives",
          body: "Operator and customer records are stored in a private database on our own infrastructure. Database backups run every 6 hours to encrypted cloud storage, retained on a 7-day, 4-week, and 3-month rotation. Signed leases, signed agreements, dock plans, lot maps, and verification documents are stored in encrypted cloud storage with signed-URL access — only the right operator and customer can fetch them.",
        },
        {
          heading: "Subprocessors",
          body: "We use Stripe for payment processing, Twilio for SMS, SendGrid for email, Cloudflare for DNS and document storage, DocuSeal for e-signatures, and per-vertical access vendors (gate, building, dock, and lot access) when you connect them. We also use Lob.com for USPS certified-mail delivery where a state requires it. Each subprocessor processes only the data we forward to them for their specific purpose; none are given general data access.",
        },
        {
          heading: "Contact",
          body: "Privacy questions and data-export-on-request: info@nestplot.com with your account email.",
        },
      ]}
    >
      <LegalFrameworkSection />
    </LegalPage>
  );
}

export function SmsTermsPage() {
  return (
    <LegalPage
      title="Text Messaging (SMS) Terms"
      path="/sms-terms"
      sections={[
        {
          heading: "About NestPlot messaging",
          body: "NestPlot (a DBA of Digideal LLC, formally Extradeall.com LLC, 16192 Coastal Highway, Lewes, DE 19958) provides property operations software to operators of self-storage, parking, RV/boat, and multifamily properties. Through NestPlot, an operator sends transactional text messages (SMS) to their own renters/tenants — and, for account security, to operators themselves. Messages are account, billing, and access notifications only — never marketing or promotional content.",
        },
        {
          heading: "What messages you may receive",
          body: "Rent and payment reminders and past-due (dunning) notices; move-in and reservation confirmations; gate, unit, or access-code messages; and one-time sign-in links you request. A representative message: “NestPlot: Rent for Unit B12 at Riverside Storage is past due — pay at https://app.nestplot.com/pay. Reply STOP to opt out.”",
        },
        {
          heading: "How you opt in",
          body: "SMS is off unless you provide a mobile number and consent. You opt in by entering your mobile number and agreeing at the point you reserve a unit or sign a lease on a NestPlot-powered rental page, or when you add/confirm your number in the tenant portal. The consent shown at signing reads: “By signing I agree … and to receive account, billing, and access notifications by email and text (SMS) at the contacts I provide — e.g. payment reminders, move-in and gate-code messages. Message frequency varies; msg & data rates may apply. Reply STOP to opt out, HELP for help.”",
        },
        {
          heading: "Frequency and fees",
          body: "Message frequency varies with your account activity — typically a few messages around move-in plus billing reminders and any access-code or sign-in messages you request. Message and data rates may apply according to your mobile carrier plan; NestPlot does not charge you for text messages.",
        },
        {
          heading: "Opt out and help",
          body: "Reply STOP to any message to stop all texts (STOP, STOPALL, UNSUBSCRIBE, CANCEL, END, and QUIT are honored); reply HELP for help, or email info@nestplot.com. You can also ask your operator to remove your number. Opting out of SMS does not affect your lease, your payments, or your access to the tenant portal.",
        },
        {
          heading: "Privacy",
          body: "Your mobile number is used only to send you these transactional messages and is never sold or shared for marketing. No mobile opt-in data is shared with third parties for their own marketing. See our Privacy policy at nestplot.com/privacy. Message delivery is best-effort; carriers are not liable for delayed or undelivered messages.",
        },
      ]}
    />
  );
}

export function TermsPage() {
  return (
    <LegalPage
      title="Terms"
      sections={[
        {
          heading: "Who you are agreeing with",
          body: "These Terms of Service are a binding agreement between you and NestPlot (“we”, “our”, “us”), a DBA of Digideal LLC, a Delaware limited liability company with a registered office at 16192 Coastal Highway, Lewes, DE 19958-9776. By creating an account or using NestPlot you agree to these Terms.",
        },
        {
          heading: "What NestPlot is",
          body: "NestPlot is a property operations platform used by operators across four verticals: multifamily (apartments, condos, senior living), self-storage, parking (monthly permits and transient), and RV/boat storage (marinas, dry stacks, RV lots). The same software supports each vertical; the lease engine, recovery workflow, billing cadence, and access integrations adapt to the governing law and the typical workflow of whichever vertical a property is configured for.",
        },
        {
          heading: "Subscription",
          body: "NestPlot is billed monthly or annually per property. The 30-day free trial requires no payment method. After day 30, your card is charged automatically unless you cancel. You may cancel from Settings → Billing at any time; cancellation takes effect at the end of the current billing period.",
        },
        {
          heading: "Operator obligations",
          body: "You are responsible for the accuracy of the customer data, access credentials, and lease templates you upload, and for the customer relationships those records represent. You must comply with all laws governing your property type in each state where you operate — including landlord-tenant law for multifamily, self-storage statute for storage, parking lease and impound law for parking, and marina/RV agreement and lien law for vehicle storage. NestPlot surfaces per-state rules and reminder schedules as a convenience, but those are advisory; final legal compliance is the operator's responsibility.",
        },
        {
          heading: "Customer access",
          body: "Paying customers always get in. We never silently revoke a paying customer's access — gate code, building fob, mobile key, dock or lot access — in response to a missed payment notice, a trial expiry, or a system error. Access turns off only on an explicit operator action or a customer move-out. By operating NestPlot you agree not to attempt to subvert this commitment.",
        },
        {
          heading: "Pricing recommendations",
          body: "The pricing AI is advisory by default. You may opt-in to auto-apply small changes from Settings → Dynamic pricing, with a configurable maximum movement per change. Every change — auto-applied or operator-approved — is recorded in your rate history audit log.",
        },
        {
          heading: "Background screening fees",
          body: "Where your facility is set to operator-paid screening, you authorize NestPlot to charge your payment method the screening fee at the time you initiate a screening — before the applicant is invited. Screenings are performed by our background-check provider and, once initiated, cannot be canceled by you or by NestPlot; the applicant may complete the screening at any time after the invitation is sent, including after you have reached a leasing decision or moved on to another applicant, and the fee remains due. The screening fee is non-refundable except where the applicant does not complete the screening and it lapses without a report being produced, in which case NestPlot refunds the fee within 14 days. Fees vary by package and are shown before you confirm. Where your facility is set to applicant-paid screening, the applicant pays the provider directly and no charge is made to you.",
        },
        {
          heading: "State-by-state availability",
          body: "NestPlot is available to operators in the United States (all 50 states and the District of Columbia), Canada, the United Kingdom, Ireland, Australia, and New Zealand, billed in the local currency of your country. Inside the United States we auto-generate a state-specific lease template from the statute that governs your vertical — landlord-tenant act, self-storage statute, parking lease law, or marina/RV agreement — with the reminder cadence, late-fee math, and recovery timing calibrated per state. Outside the United States, NestPlot does not yet auto-generate statutory lease templates or drive statutory lien-sale / notice windows — your lease documents and any recovery action remain yours; see your country's framework in the section below. We are not a law firm and these defaults are not legal advice — confirm the current law with your counsel before relying on any specific rule.",
        },
        {
          heading: "Compliance items the operator handles",
          body: "A handful of compliance responsibilities stay with you regardless of state and vertical: (a) any insurance-licensed product you offer alongside the lease — for example, tenant-protection plans in self-storage, renter's insurance for multifamily, or hull insurance verification for marinas — NestPlot may display the operator's terms and capture the customer's election, but does not underwrite and does not act as a licensed producer; (b) sales tax, parking surcharges, and any other transaction tax: NestPlot collects, itemizes, and reports the right amount on receipts and the P&L export, but the operator files the return; and (c) the final step of any recovery action — lien-sale auction in self-storage, court eviction in multifamily, tow / impound and release in parking, vessel detention or auction in marinas — runs through the operator and any third-party platforms or service providers they choose. NestPlot drives the notice windows and produces the required documents up to that point.",
        },
        {
          heading: "Termination",
          body: "Either party may terminate the agreement for material breach. On termination, you may export all your data as CSV. We will purge it from our systems within 30 days of confirmed export.",
        },
        {
          heading: "Intellectual property",
          body: "The NestPlot name, logo, design system, source code, and all related trademarks are owned by Digideal LLC. Nothing in these Terms transfers any of those rights to you. You retain ownership of every value, photo, document, and customer record you upload.",
        },
        {
          heading: "Indemnification",
          body: "You agree to defend, indemnify, and hold harmless NestPlot, Digideal LLC, and their respective officers, employees, and agents from any claim arising out of your use of the service, your breach of these Terms, or your violation of any law applicable to the property types you operate — including landlord-tenant law, self-storage statutes, parking lease and impound law, and marina or RV agreement and lien law — in any state where you operate.",
        },
      ]}
    >
      <LegalFrameworkSection />
    </LegalPage>
  );
}
